import axios from 'axios';
import { API_ENDPOINTS } from './api.config';

export interface GetRolesParams {
  page?: number;
  per_page?: number;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions?: string[]; // Array of permission values (e.g., ["read:users"])
}

export interface CreateRoleData {
  name: string;
  description: string;
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
}

export interface GetRolesResponse {
  roles?: Role[]; // API might return roles array directly
  data?: Role[]; // Or wrapped in data property
  total?: number;
  page?: number;
  per_page?: number;
}

type RoleLike = Record<string, unknown>;

/** Extract role id from API payloads (`id`, `role_id`, `roleId`, `_id`). */
export function getRoleIdFromApi(
  role: RoleLike | null | undefined,
): string {
  if (!role) return '';
  const raw =
    role.id ?? role.role_id ?? role.roleId ?? role._id;
  return raw != null && String(raw).trim() !== '' ? String(raw) : '';
}

export function normalizeRoleFromApi(role: RoleLike): Role {
  return {
    id: getRoleIdFromApi(role),
    name: String(role.name ?? ''),
    description: String(role.description ?? ''),
    permissions: Array.isArray(role.permissions)
      ? (role.permissions as string[])
      : undefined,
  };
}

function normalizeRolesList(items: unknown[]): Role[] {
  return items
    .filter((item): item is RoleLike => item != null && typeof item === 'object')
    .map(normalizeRoleFromApi);
}

/**
 * Get all roles with pagination
 */
export const getAllRoles = async (
  params: GetRolesParams = {},
  token: string,
): Promise<GetRolesResponse> => {
  const { page = 0, per_page = 10 } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
  });

  const response = await axios.get(`${API_ENDPOINTS.ROLES}?${queryParams}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  // Handle different response structures
  const responseData = response.data;
  if (Array.isArray(responseData)) {
    return { roles: normalizeRolesList(responseData) };
  }
  if (Array.isArray(responseData.roles)) {
    return {
      ...responseData,
      roles: normalizeRolesList(responseData.roles),
    };
  }
  if (Array.isArray(responseData.data)) {
    return {
      ...responseData,
      roles: normalizeRolesList(responseData.data),
      data: normalizeRolesList(responseData.data),
    };
  }
  return responseData;
};

/**
 * Create a new role
 */
export const createRole = async (
  data: CreateRoleData,
  token: string,
): Promise<Role> => {
  const response = await axios.post(API_ENDPOINTS.ROLE, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  // Log the raw response for debugging
  // eslint-disable-next-line no-console
  console.log('Create role API response:', response.data);

  // Handle different response structures
  const responseData = response.data;
  const raw =
    responseData?.role ??
    responseData?.data ??
    responseData;

  return normalizeRoleFromApi(
    raw && typeof raw === 'object' ? (raw as RoleLike) : {},
  );
};

/**
 * Update a role
 */
export const updateRole = async (
  roleId: string,
  data: UpdateRoleData,
  token: string,
): Promise<Role> => {
  const id = String(roleId ?? '').trim();
  if (!id) {
    throw new Error('Role ID is required');
  }

  const response = await axios.put(
    `${API_ENDPOINTS.ROLE}/${encodeURIComponent(id)}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  const responseData = response.data;
  const raw =
    responseData?.role ??
    responseData?.data ??
    responseData;

  const updated = normalizeRoleFromApi(
    raw && typeof raw === 'object'
      ? { ...(raw as RoleLike), ...data, id: getRoleIdFromApi(raw as RoleLike) || id }
      : { ...data, id },
  );

  return updated.id ? updated : { ...updated, id };
};

/**
 * Delete a role
 */
export const deleteRole = async (
  roleId: string,
  token: string,
): Promise<void> => {
  const id = String(roleId ?? '').trim();
  if (!id) {
    throw new Error('Role ID is required');
  }

  await axios.delete(`${API_ENDPOINTS.ROLE}/${encodeURIComponent(id)}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Remove permissions from a role
 */
export const removePermissionsFromRole = async (
  roleId: string,
  permissionIds: string[],
  token: string,
): Promise<void> => {
  const id = String(roleId ?? '').trim();
  if (!id) {
    throw new Error('Role ID is required');
  }

  await axios.delete(`${API_ENDPOINTS.ROLE}/${encodeURIComponent(id)}/permissions`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: { permissions: permissionIds },
  });
};

/**
 * Get role permissions
 */
export const getRolePermissions = async (
  roleId: string,
  token: string,
): Promise<string[]> => {
  const id = String(roleId ?? '').trim();
  if (!id) {
    throw new Error('Role ID is required');
  }

  const response = await axios.get(
    `${API_ENDPOINTS.ROLE}/${encodeURIComponent(id)}/permissions`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  // Assuming the response contains permission IDs
  return response.data?.permissions || response.data?.data || [];
};
